import React, {createContext, useCallback, useContext, useEffect, useRef, useState} from "react";
import moment from "moment";
import {ConversationRepository} from "../repositories/ConversationRepository";
import {
    DashboardRepository,
    DEFAULT_RESPONSE_TIME_PERIOD,
    RESPONSE_TIME_TYPES,
    resolvePeriod,
    STATISTIC_PERIODS
} from "../repositories/DashboardRepository";
import {useStompWebsocket} from "../hooks/useStompWebsocket";

export const DashboardContext = createContext({
    responseTime: {},
    responseTimeWindows: {},
    setResponseTimePeriod: () => {
    },
    shiftResponseTime: () => {
    },
    activeCalls: 0,
    refreshActiveCalls: () => {
    },
    statuses: {},
    conversations: [],
    pagination: {page: 0, size: 25, totalElements: 0},
    nodeUsage: {},
    wsState: "inactive",
    updatedValue: null,
    tagUsage: {},
    tagStatistic: null,
    dateRange: null,
    setDateRange: () => {
    },
    refreshInterval: null,
    setRefreshInterval: () => {
    },
    lastFetched: null,
    refreshResponseTime: () => {
    },
    refreshStatuses: () => {
    },
    refreshConversations: () => {
    },
    refreshNodeUsage: () => {
    },
    refreshTagsUsage: () => {
    },
    refreshAll: () => {
    },
    setPagination: () => {
    },
});
// Turns a card's stored window ({period, end}) into the period and start/end to request
const resolveResponseTimeWindow = (window) => {
    const period = window?.period || DEFAULT_RESPONSE_TIME_PERIOD;
    const {window: size} = STATISTIC_PERIODS.find(p => p.value === period);
    const end = window?.end ? window.end.clone() : moment();
    return {period, start: end.clone().subtract(size.amount, size.unit), end};
};

const resolveRange = (dateRange) => {
    if (dateRange?.start && dateRange?.end) {
        return {start: dateRange.start, end: dateRange.end};
    }
    const end = moment();
    return {start: end.clone().subtract(24, "hours"), end};
};

export const DashboardContextProvider = ({project, children}) => {
    const [updatedValue, wsState] = useStompWebsocket(project?.id ? `/topic/flow/${project?.id}` : null);

    const [responseTime, setResponseTime] = useState({});
    const [activeCalls, setActiveCalls] = useState(0);
    const [statuses, setStatuses] = useState({});
    const [conversations, setConversations] = useState([]);
    const [pagination, setPagination] = useState({page: 0, size: 25, totalElements: 0});
    const [nodeUsage, setNodeUsage] = useState({});
    const [tagUsage, setTagUsage] = useState({});
    // tagStatistic: FlowTagStatisticDto | null — full response incl. per-bucket counts
    const [tagStatistic, setTagStatistic] = useState(null);
    const [tagsUsageLoading, setTagsUsageLoading] = useState(true);
    // dateRange: {label, start, end} (moment instances) | null — applies to stats only, NOT to the conversations list
    const [dateRange, setDateRange] = useState(null);
    // refreshInterval: milliseconds | 60000 — how often stats (NOT conversations) auto-refetch
    const [refreshInterval, setRefreshInterval] = useState(60000);
    // lastFetched: Date | null — when the stats (NOT conversations) were last successfully refreshed
    const [lastFetched, setLastFetched] = useState(null);

    // Each response-time card has its own window, independent of the dashboard dateRange:
    // {[type]: {period, end}} where end is a moment, or null while the window ends now (live).
    // Kept in a ref so changing one card refetches only that card instead of every stat.
    const responseTimeWindowsRef = useRef({});
    // Same windows as state, for the cards to show
    const [responseTimeWindows, setResponseTimeWindows] = useState({});
    // Latest request per card, so a slower older response can't overwrite a newer one
    const responseTimeRequestsRef = useRef({});

    const loadResponseTime = useCallback(async (type, signal) => {
        if (!project?.id) return;

        const requestId = (responseTimeRequestsRef.current[type] || 0) + 1;
        responseTimeRequestsRef.current[type] = requestId;

        const {period, start, end} = resolveResponseTimeWindow(responseTimeWindowsRef.current[type]);
        try {
            const response = await DashboardRepository.getResponseTime({
                flowUid: project.id,
                type,
                period,
                start: start.format("YYYY-MM-DDTHH:mm:ss"),
                end: end.format("YYYY-MM-DDTHH:mm:ss"),
                signal
            });
            if (responseTimeRequestsRef.current[type] === requestId) {
                setResponseTime(prev => ({...prev, [type]: response?.data}));
            }
        } catch (error) {
            if (error.name !== "CanceledError" && error.name !== "AbortError") {
                console.error(`Error fetching ${type} response time:`, error);
            }
        }
    }, [project?.id]);

    // Fetch response time data — one request per metric, stored as {[type]: FlowResponseTimeDto}
    const refreshResponseTime = useCallback((signal) =>
            Promise.all(RESPONSE_TIME_TYPES.map(type => loadResponseTime(type, signal))),
        [loadResponseTime]);

    const updateResponseTimeWindow = useCallback((type, changes) => {
        responseTimeWindowsRef.current = {
            ...responseTimeWindowsRef.current,
            [type]: {...responseTimeWindowsRef.current[type], ...changes}
        };
        setResponseTimeWindows(responseTimeWindowsRef.current);
        return loadResponseTime(type);
    }, [loadResponseTime]);

    // Picking a period jumps back to the live window (ending now)
    const setResponseTimePeriod = useCallback((type, period) =>
        updateResponseTimeWindow(type, {period, end: null}), [updateResponseTimeWindow]);

    // direction: -1 moves the window back, 1 forward, by half a window; never past now
    const shiftResponseTime = useCallback((type, direction) => {
        const {period, end} = resolveResponseTimeWindow(responseTimeWindowsRef.current[type]);
        const {step} = STATISTIC_PERIODS.find(p => p.value === period);
        const nextEnd = direction < 0
            ? end.clone().subtract(step.amount, step.unit)
            : end.clone().add(step.amount, step.unit);

        return updateResponseTimeWindow(type, {end: nextEnd.isBefore(moment()) ? nextEnd : null});
    }, [updateResponseTimeWindow]);

    // Fetch status statistics (conversation status gauge) — page load and interval only
    const refreshStatuses = useCallback(async (signal) => {
        if (!project?.id) return;

        const range = resolveRange(dateRange);
        try {
            const response = await ConversationRepository.getStatusStatistic({
                flowUid: project.id,
                type: project?.settings?.projectType || "VOICE_BOT",
                start: range.start.format("YYYY-MM-DDTHH:mm:ss"),
                end: range.end.format("YYYY-MM-DDTHH:mm:ss"),
                signal
            });
            setStatuses(response?.data || {});
        } catch (error) {
            if (error.name !== "CanceledError" && error.name !== "AbortError") {
                console.error("Error fetching statuses:", error);
            }
        }
    }, [project?.id, dateRange]);

    // Fetch the number of calls active right now — not affected by dateRange
    const refreshActiveCalls = useCallback(async (signal) => {
        if (!project?.id) return;

        try {
            const response = await DashboardRepository.getActiveCalls({
                flowUid: project.id,
                conversationType: project?.settings?.projectType || "VOICE_BOT",
                signal
            });
            setActiveCalls(Number(response?.data) || 0);
        } catch (error) {
            if (error.name !== "CanceledError" && error.name !== "AbortError") {
                console.error("Error fetching active calls:", error);
            }
        }
    }, [project?.id, project?.settings?.projectType]);


    // Fetch conversations — intentionally NOT affected by dateRange
    const refreshConversations = useCallback(async (filters = {}) => {
        if (!project?.id) return;

        try {
            const response = await ConversationRepository.getConversations({
                flow: project.id,
                page: pagination?.page || 0,
                size: pagination?.size || 25,
                type: project?.settings?.projectType || "VOICE_BOT",
                ...filters,
            });

            setPagination(prev => ({
                ...prev,
                totalElements: response?.data?.totalElements || 0
            }));
            setConversations(response?.data?.content || []);
        } catch (error) {
            console.error("Error fetching conversations:", error);
        }
    }, [project?.id, pagination?.page, pagination?.size]);

    // Fetch node usage data
    const refreshNodeUsage = useCallback(async () => {
        if (!project?.id) return;

        const tags = project?.settings?.tags || [];
        const ids = tags?.map(tag => tag?.nodes || [])?.flat(1);

        if (ids.length === 0) {
            setNodeUsage({});
            return;
        }
        const range = resolveRange(dateRange);
        try {
            const response = await ConversationRepository.getNodeUsage(ids, {
                start: range.start.format("YYYY-MM-DDTHH:mm:ss"),
                end: range.end.format("YYYY-MM-DDTHH:mm:ss"),
            });
            setNodeUsage(response?.data || {});
        } catch (error) {
            console.error("Error fetching node usage:", error);
        }
    }, [project?.id, project?.settings?.tags, dateRange]);

    // Range picked in the tags chart itself, remembered so interval refreshes keep using it.
    // It only applies while the global dateRange it was picked under is still active.
    const tagsRangeRef = useRef(null);

    // Called with start/end when the tags chart picks a range, without them on interval refreshes
    const refreshTagsUsage = useCallback(async (start, end) => {
        if (!project?.id) return;

        const isBackgroundRefresh = start === undefined && end === undefined;
        if (!isBackgroundRefresh) {
            tagsRangeRef.current = {start, end, dateRange};
        } else if (tagsRangeRef.current?.dateRange === dateRange) {
            start = tagsRangeRef.current.start;
            end = tagsRangeRef.current.end;
        }

        // The API keys counts by tag uid (tag.id in project settings)
        const tagNamesById = {};
        for (const tag of project?.settings?.tags || []) {
            if (tag?.id && tag?.name) tagNamesById[tag.id] = tag.name;
        }

        if (Object.keys(tagNamesById).length === 0) {
            setTagUsage({});
            setTagStatistic(null);
            setTagsUsageLoading(false);
            return;
        }

        const base = dateRange?.start && dateRange?.end
            ? resolveRange(dateRange)
            : {start: moment().startOf("day"), end: moment().endOf("day")};
        const rangeStart = start !== undefined ? start : base.start;
        const rangeEnd = end !== undefined ? end : base.end;

        // Background refreshes update the chart in place instead of flashing "Loading..."
        if (!isBackgroundRefresh) setTagsUsageLoading(true);

        try {
            const response = await DashboardRepository.getTagStatistic({
                flowUid: project.id,
                conversationType: project?.settings?.projectType || "VOICE_BOT",
                period: rangeStart && rangeEnd ? resolvePeriod(rangeStart, rangeEnd) : undefined,
                start: rangeStart ? rangeStart.format("YYYY-MM-DDTHH:mm:ss") : undefined,
                end: rangeEnd ? rangeEnd.format("YYYY-MM-DDTHH:mm:ss") : undefined
            });
            // tagUsage is {tagName: count} for the chart and gauge; uids of tags no longer
            // in the project are skipped
            const usage = {};
            for (const [uid, count] of Object.entries(response?.data?.totals || {})) {
                const name = tagNamesById[uid];
                if (name) usage[name] = (usage[name] || 0) + (Number(count) || 0);
            }
            setTagUsage(usage);
            setTagStatistic(response?.data || null);
        } catch (error) {
            console.error("Error fetching tags usage:", error);
        } finally {
            setTagsUsageLoading(false);
        }
    }, [project?.id, project?.settings?.tags, dateRange]);

    // Range-based stats, refreshed on the selected interval and when the date range changes.
    // Active calls are not included — they follow the websocket instead.
    const refreshDashboardStats = useCallback(async (signal) => {
        await Promise.all([
            refreshResponseTime(signal),
            refreshStatuses(signal),
            refreshNodeUsage(),
            refreshTagsUsage()
        ]);
    }, [
        refreshResponseTime,
        refreshStatuses,
        refreshNodeUsage,
        refreshTagsUsage
    ]);

    // Active calls: load once per project, then on websocket changes
    useEffect(() => {
        if (!project?.id) return;

        const controller = new AbortController();
        refreshActiveCalls(controller.signal);
        return () => controller.abort();
    }, [project?.id, refreshActiveCalls]);

    // On a websocket change, refresh active calls 3s later (gives the backend time to save).
    // Throttled rather than debounced: messages arriving while a refresh is pending are
    // covered by it, so a steady stream of messages can't postpone it forever.
    const wsRefreshTimeoutRef = useRef(null);
    const lastWsValueRef = useRef(null);

    useEffect(() => {
        if (!project?.id || !updatedValue || updatedValue === lastWsValueRef.current) return;
        lastWsValueRef.current = updatedValue;
        if (wsRefreshTimeoutRef.current) return;

        wsRefreshTimeoutRef.current = setTimeout(() => {
            wsRefreshTimeoutRef.current = null;
            refreshActiveCalls().then(() => setLastFetched(new Date()));
        }, 3000);
    }, [
        project?.id,
        updatedValue,
        refreshActiveCalls
    ]);

    useEffect(() => () => clearTimeout(wsRefreshTimeoutRef.current), []);

    // Refresh all data

    const refreshAll = useCallback(async () => {
        const controller = new AbortController();

        await Promise.all([
            refreshDashboardStats(controller.signal),
            refreshActiveCalls(controller.signal),
            refreshConversations()
        ]);

        return () => controller.abort();
    }, [
        refreshDashboardStats,
        refreshActiveCalls,
        refreshConversations
    ]);

    // Re-fetch stats (NOT conversations) whenever the date range changes
    useEffect(() => {
        if (!project?.id) return;

        const controller = new AbortController();

        refreshDashboardStats(controller.signal)
            .then(() => setLastFetched(new Date()));

        return () => controller.abort();
    }, [dateRange, project?.id, refreshDashboardStats]);

    // Periodically re-fetch stats (NOT conversations) at the chosen interval
    useEffect(() => {
        if (!project?.id || !refreshInterval) return;

        const intervalId = setInterval(() => {
            const controller = new AbortController();
            refreshDashboardStats(controller.signal)
                .then(() => setLastFetched(new Date()));
        }, refreshInterval);

        return () => clearInterval(intervalId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [project?.id, refreshInterval, dateRange, refreshDashboardStats]);

    const value = {
        responseTime,
        responseTimeWindows,
        setResponseTimePeriod,
        shiftResponseTime,
        activeCalls,
        refreshActiveCalls,
        statuses,
        conversations,
        pagination,
        nodeUsage,
        wsState,
        tagUsage,
        tagStatistic,
        tagsUsageLoading,
        updatedValue,
        dateRange,
        setDateRange,
        refreshInterval,
        setRefreshInterval,
        lastFetched,
        refreshResponseTime,
        refreshStatuses,
        refreshConversations,
        refreshNodeUsage,
        refreshTagsUsage,
        refreshAll,
        setPagination,
        refreshDashboardStats
    };

    return (
        <DashboardContext.Provider value={value}>
            {children}
        </DashboardContext.Provider>
    );
};

export const useDashboard = () => {
    const context = useContext(DashboardContext);
    if (!context) {
        throw new Error("useDashboard must be used within DashboardContextProvider");
    }
    return context;
};
